import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import PageShell from "../../components/public/PageShell";
import Pagination from "../../components/public/Pagination";
import { CalendarBlank, User, ArrowRight, Newspaper, Funnel } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import useReveal from "../../hooks/useReveal";
import usePageMeta from "../../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PER_PAGE = 9;

export default function BlogsList() {
  usePageMeta({
    title: "Insights — Bon-Heur Pharmaceuticals",
    description: "Science, stories and pharma perspectives from Bon-Heur Pharmaceuticals. Research notes, clinical updates and behind-the-scenes from our Haridwar labs.",
    keywords: "Bon-Heur insights, pharmaceutical blog, clinical research, pharma news, drug formulations",
  });
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const activeCat = params.get("category") || "";

  useEffect(() => {
    axios.get(`${API}/public/categories?kind=blog`).then((r) => setCats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setPage(1);
    const url = new URL(`${API}/public/blogs`);
    if (activeCat) url.searchParams.set("category", activeCat);
    axios.get(url.toString()).then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, [activeCat]);

  const setCategory = (slug) => {
    const p = new URLSearchParams(params);
    if (slug) p.set("category", slug); else p.delete("category");
    setParams(p);
  };

  // hero is now derived per-page inside the render

  return (
    <PageShell pageKey="blogs" eyebrow="Insights" title="Science, stories and pharma perspectives" subtitle="Stay current with research notes, clinical updates and behind-the-scenes from our labs.">
      <section className="py-14 lg:py-20 bg-white" data-testid="blogs-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10" data-testid="blog-categories">
            <p className="text-sm text-[#475569]">
              {items.length === 0 ? "No articles yet" : `${items.length} article${items.length === 1 ? "" : "s"}${activeCat ? ` in this category` : ""}`}
            </p>
            <div className="flex items-center gap-3">
              <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-[#64748B] uppercase tracking-[0.2em] font-semibold">
                <Funnel size={12} weight="bold" /> Category
              </span>
              <Select value={activeCat || "__all__"} onValueChange={(v) => setCategory(v === "__all__" ? "" : v)}>
                <SelectTrigger
                  data-testid="blog-category-select"
                  className="w-[240px] h-11 rounded-full border-[#E2E8F0] bg-white text-sm font-semibold text-[#1F2937] hover:border-[#C2410C] focus:ring-2 focus:ring-[#C2410C]/20 px-5"
                >
                  <SelectValue placeholder="All articles" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="__all__" data-testid="blog-cat-all">All articles</SelectItem>
                  {cats.map((c) => (
                    <SelectItem key={c.id} value={c.slug} data-testid={`blog-cat-${c.slug}`}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[4/5] rounded-2xl bg-[#F1F5F9] animate-pulse" />)}</div>
          ) : items.length === 0 ? (
            <div className="py-24 text-center text-[#64748B]"><Newspaper size={36} className="mx-auto text-[#CBD5E1] mb-3" />No articles yet — check back soon.</div>
          ) : (
            <>
              {(() => {
                const start = (page - 1) * PER_PAGE;
                const pageItems = items.slice(start, start + PER_PAGE);
                const heroOnPage = page === 1 ? pageItems[0] : null;
                const gridItems = page === 1 ? pageItems.slice(1) : pageItems;
                return (
                  <>
                    {heroOnPage && <HeroCard b={heroOnPage} />}
                    {gridItems.length > 0 && (
                      <div className={`${heroOnPage ? "mt-10" : ""} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6`} data-testid="blogs-grid">
                        {gridItems.map((b, i) => <BlogCard key={b.id} b={b} i={i} />)}
                      </div>
                    )}
                  </>
                );
              })()}
              <Pagination
                page={page}
                pageCount={Math.ceil(items.length / PER_PAGE)}
                onChange={(p) => { setPage(p); window.scrollTo({ top: 200, behavior: "smooth" }); }}
                testIdPrefix="blogs-pagination"
              />
            </>
          )}
        </div>
      </section>
    </PageShell>
  );
}

function blogImg(b) {
  if (!b?.cover_url) return null;
  return b.cover_url.startsWith("http") ? b.cover_url : `${BACKEND}${b.cover_url}`;
}

function HeroCard({ b }) {
  const img = blogImg(b);
  const date = b.created_at ? new Date(b.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : null;
  return (
    <Link to={`/blogs/${b.slug}`} className="group grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center bg-[#F8FAFC] rounded-3xl overflow-hidden border border-[#E2E8F0] hover:border-[#C2410C] transition-all duration-300" data-testid="blog-hero">
      <div className="relative aspect-[16/10] lg:aspect-auto lg:h-full overflow-hidden bg-[#0F172A]">
        {img ? <img src={img} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full bg-gradient-to-br from-[#C2410C]/20 to-[#0EA5E9]/20" />}
      </div>
      <div className="p-8 lg:p-10">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#C2410C] font-semibold">Latest insight</div>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1F2937] group-hover:text-[#C2410C] transition-colors leading-[1.15]" style={{ fontFamily: "'Manrope', sans-serif" }}>{b.title}</h2>
        {b.excerpt && <p className="mt-4 text-[#475569] leading-relaxed line-clamp-3">{b.excerpt}</p>}
        <div className="mt-6 flex items-center gap-5 text-xs text-[#64748B]">
          {b.author && <span className="inline-flex items-center gap-1"><User size={12} weight="bold" /> {b.author}</span>}
          {date && <span className="inline-flex items-center gap-1"><CalendarBlank size={12} weight="bold" /> {date}</span>}
        </div>
        <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#C2410C]">Read article <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-1" /></div>
      </div>
    </Link>
  );
}

function BlogCard({ b, i }) {
  const { ref, visible } = useReveal();
  const img = blogImg(b);
  const date = b.created_at ? new Date(b.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : null;
  return (
    <div
      ref={ref}
      data-testid={`blog-card-${b.slug}`}
      className="transition-all duration-700"
      style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(24px)", transitionDelay: `${(i % 6) * 80}ms` }}
    >
      <Link to={`/blogs/${b.slug}`} className="group block">
        <div className="relative aspect-[16/10] rounded-2xl overflow-hidden bg-[#0F172A] border border-[#E2E8F0] group-hover:border-[#C2410C] transition-colors">
          {img ? <img src={img} alt={b.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full bg-gradient-to-br from-[#C2410C]/20 to-[#0EA5E9]/20" />}
        </div>
        <h3 className="mt-4 text-lg font-semibold text-[#1F2937] group-hover:text-[#C2410C] transition-colors line-clamp-2" style={{ fontFamily: "'Manrope', sans-serif" }}>{b.title}</h3>
        {b.excerpt && <p className="mt-2 text-sm text-[#64748B] line-clamp-2">{b.excerpt}</p>}
        <div className="mt-3 flex items-center gap-4 text-[11px] text-[#94A3B8]">
          {b.author && <span>{b.author}</span>}
          {date && <span>·</span>}
          {date && <span>{date}</span>}
        </div>
      </Link>
    </div>
  );
}
